import { useCallback, useState } from 'react'
import type { ShapeType } from '@/lib/shapes/types/shape-type'

/** Prop-controlled when the parent provides a setter, otherwise local state. */
function useControllableValue<T>(
  value: T | null | undefined,
  setValue: ((next: T | null) => void) | undefined
): [T | null, (next: T | null) => void] {
  const [localValue, setLocalValue] = useState<T | null>(null)

  return setValue != null ? [value ?? null, setValue] : [localValue, setLocalValue]
}

interface UseShapeToolStateInput {
  selectedShape?: ShapeType | null
  setSelectedShape?: (shape: ShapeType | null) => void
  selectedPenTool?: string | null
  setSelectedPenTool?: (tool: string | null) => void
  selectedPointType?: string | null
  setSelectedPointType?: (type: string | null) => void
}

/** Shapes tool state (preset shape, pen tool, point type) plus the shared "exit shape creation" reset. */
export function useShapeToolState(input: UseShapeToolStateInput) {
  const [selectedShape, setSelectedShape] = useControllableValue(
    input.selectedShape,
    input.setSelectedShape
  )
  const [selectedPenTool, setSelectedPenTool] = useControllableValue(
    input.selectedPenTool,
    input.setSelectedPenTool
  )
  const [selectedPointType, setSelectedPointType] = useControllableValue(
    input.selectedPointType,
    input.setSelectedPointType
  )

  // Switching tools exits shape creation mode: clears the chosen preset shape and pen tool.
  const resetShapeCreation = useCallback(() => {
    setSelectedShape(null)
    setSelectedPenTool(null)
  }, [setSelectedShape, setSelectedPenTool])

  return {
    selectedShape,
    setSelectedShape,
    selectedPenTool,
    setSelectedPenTool,
    selectedPointType,
    setSelectedPointType,
    resetShapeCreation,
  }
}
